import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { PLAN_CONFIG } from '../config/plans';
import billingService from '../services/billingService';
import { useAuth } from '../context/AuthContext';

const proPlan = PLAN_CONFIG.pro;

const UpgradeModal = ({ isOpen, onClose, onRequireAuth }) => {
  const { isAuthenticated, sessionUser } = useAuth();
  const [loading, setLoading] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const handleUpgrade = async (interval) => {
    if (!isAuthenticated) {
      if (onRequireAuth) onRequireAuth();
      return;
    }

    setLoading(interval);
    try {
      const session = await billingService.createCheckoutSession(interval);
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to start checkout:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const session = await billingService.createPortalSession();
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to open billing portal:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white shadow-brand-lg"
            initial={{ scale: 0.96, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between border-b border-[rgba(32,30,29,0.12)] p-6">
              <div className="flex items-center gap-2 text-ink">
                <Sparkles className="h-5 w-5 text-bill-600" />
                <h2 className="text-lg font-semibold">Upgrade to Pro</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-sand-600 hover:bg-sand-100 hover:text-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 text-sm text-sand-700 space-y-4">
              <div className="rounded-xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-4 text-sand-700">
                <div className="text-xs uppercase text-sand-600">Pro plan</div>
                <div className="mt-2 text-2xl font-semibold text-ink">
                  ${proPlan.priceMonthly}
                  <span className="text-sm font-normal text-sand-600">/month</span>
                </div>
                <div className="text-xs text-sand-600">or ${proPlan.priceYearly}/year</div>
                <div className="mt-3 text-xs text-sand-600">
                  Higher limits, automation workflows, and team features.
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {sessionUser?.plan_type === 'pro' ? (
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={billingLoading}
                    className="w-full rounded-full bg-bill-500 px-4 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm disabled:opacity-50"
                  >
                    {billingLoading ? 'Opening billing...' : 'Manage billing'}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleUpgrade('monthly')}
                      disabled={loading === 'monthly'}
                      className="w-full rounded-full bg-bill-500 px-4 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm disabled:opacity-50"
                    >
                      {loading === 'monthly' ? 'Starting checkout...' : `Upgrade monthly ($${proPlan.priceMonthly})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpgrade('yearly')}
                      disabled={loading === 'yearly'}
                      className="w-full rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-sand-100 disabled:opacity-50"
                    >
                      {loading === 'yearly' ? 'Starting checkout...' : `Upgrade yearly ($${proPlan.priceYearly})`}
                    </button>
                  </>
                )}
              </div>

              {!isAuthenticated && (
                <p className="text-xs text-sand-600">
                  Sign in first to complete checkout.
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
